import type { ThemeColor } from '@vrplatform/theme';
import { sortBy } from '@vrplatform/utils';
import { Box, Popover } from '@mantine/core';
import type { useDisclosure } from '@mantine/hooks';
import { useCallback, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { CustomAction, PinnedItems, SelectValueProps } from './_types';
import type { SelectItem } from './_types';
import {
  SelectDropdown,
  type DropdownProps,
  type InfiniteDataProps,
} from './SelectDropdown';

type BasicData<TSelectValue extends string> = {
  loading?: boolean;
  error?: Error | null;
  options: SelectItem<TSelectValue>[];
  sort?: 'asc' | 'desc' | null;
  onSearchChange?: (search: string) => void;
};

export type SelectDataProps<TSelectValue extends string> =
  | {
      data: BasicData<TSelectValue>;
      infiniteData?: undefined;
    }
  | {
      data?: undefined;
      infiniteData: InfiniteDataProps<TSelectValue>;
    };

export type SelectProps<TSelectValue extends string> = {
  dropdownProps?: DropdownProps;
  inputProps?: InputProps;
  onOpenChange?: (opened: boolean) => void;
  'data-testid'?: string;
  name?: string;
  id?: string;
} & SelectValueProps<TSelectValue> &
  SelectDataProps<TSelectValue> &
  CustomAction &
  PinnedItems;

type InputProps = {
  placeholder?: string;
  placeholderColor?: string;
  loadingQuery?: boolean;
  loadingMutation?: boolean;
  withClearButton?: boolean;
  withBorder?: boolean;
  disabled?: boolean;
  width?: number;
  error?: boolean | string;
  backgroundColor?: string;
  lineClamp?: number;
  color?: ThemeColor;
};

export type SelectInputProps<TSelectValue extends string> = InputProps & {
  opened: boolean;
  handlers: ReturnType<typeof useDisclosure>[1];
} & SelectValueProps<TSelectValue>;

export const Select = <TSelectValue extends string = string>({
  value,
  setValue,
  type,
  dropdownProps,
  inputProps,
  onOpenChange,
  children: Children,
  'data-testid': dataTestId,
  name,
  id,
  ...rest
}: SelectProps<TSelectValue> & {
  children: (props: SelectInputProps<TSelectValue>) => JSX.Element;
  'data-testid'?: string;
}) => {
  const [opened, setOpened] = useState(false);
  const setOpenedWithCallback = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setOpened((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        if (resolved !== prev) {
          onOpenChange?.(resolved);
        }
        return resolved;
      });
    },
    [onOpenChange]
  );

  const handleSetValue = (updated: any, value: any) => {
    setValue(updated, value);

    if (Array.isArray(updated) && updated.length === 0) {
      return setOpenedWithCallback(false);
    }

    if (!dropdownProps?.preventCloseOnSelect) {
      setOpenedWithCallback(false);
    }
  };

  const onChange = useCallback(
    (v: boolean) => {
      setOpenedWithCallback(v);
      rest.infiniteData?.setSearch?.('');
    },
    [rest.infiniteData?.setSearch, setOpenedWithCallback]
  );

  const customActionTop = useMemo(
    () =>
      rest.customActionTop
        ? {
            ...rest.customActionTop,
            onSubmit: (search: string) => {
              rest.customActionTop?.onSubmit?.(search);
              setOpenedWithCallback(false);
            },
          }
        : undefined,
    [rest.customActionTop, setOpenedWithCallback]
  );

  const customBottomActions = useMemo(
    () =>
      rest.customBottomActions
        ? rest.customBottomActions.map((action) => ({
            ...action,
            onSubmit: (search: string) => {
              action.onSubmit?.(search);
              setOpenedWithCallback(false);
            },
          }))
        : undefined,
    [rest.customBottomActions, setOpenedWithCallback]
  );

  const handlers = useMemo(
    () => ({
      toggle: () => setOpenedWithCallback((v) => !v),
      close: () => setOpenedWithCallback(false),
      open: () => setOpenedWithCallback(true),
    }),
    [setOpenedWithCallback]
  );

  const toggleOpened = useCallback(() => {
    if (!inputProps?.disabled) {
      setOpenedWithCallback((current) => !current);
    }
  }, [inputProps?.disabled, setOpenedWithCallback]);

  return (
    <Popover
      withinPortal={dropdownProps?.withinPortal}
      opened={inputProps?.loadingMutation ? false : opened}
      onChange={onChange}
      shadow="md"
      radius="md"
      disabled={inputProps?.disabled}
      width={dropdownProps?.width || 300}
      position={dropdownProps?.position}
      zIndex={dropdownProps?.zIndex}
    >
      <Popover.Target>
        <Box
          data-testid={dataTestId}
          aria-label={name || inputProps?.placeholder}
          aria-disabled={inputProps?.disabled || undefined}
          id={id}
          onKeyDown={(event) => {
            if (inputProps?.disabled) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;

            event.preventDefault();
            event.stopPropagation();
            toggleOpened();
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleOpened();
          }}
          role="button"
          tabIndex={inputProps?.disabled ? -1 : 0}
        >
          {Children({
            type,
            value: value as any,
            setValue: handleSetValue,
            opened,
            handlers,
            ...inputProps,
          })}
        </Box>
      </Popover.Target>
      <Popover.Dropdown
        data-dropdowntestid={dataTestId || id || name}
        sx={{
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {!rest.data ? (
          <SelectDropdown
            value={value}
            setValue={handleSetValue}
            {...dropdownProps}
            {...rest.infiniteData}
            {...rest}
            customActionTop={customActionTop}
            customBottomActions={customBottomActions}
          />
        ) : (
          <BasicOptions
            value={value as any}
            setValue={handleSetValue}
            dropdownProps={dropdownProps}
            {...rest}
            data={rest.data}
            type={type}
            customActionTop={customActionTop}
            customBottomActions={customBottomActions}
          />
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

const BasicOptions = <TSelectValue extends string>({
  value,
  setValue,
  data: dataProps,
  dropdownProps,
  // type,
  customBottomActions,
  customActionTop,
  pinnedItems,
}: {
  data: BasicData<TSelectValue>;
  dropdownProps: DropdownProps | undefined;
} & SelectValueProps<TSelectValue> &
  CustomAction &
  PinnedItems) => {
  const [search, setSearch] = useState('');
  const trimmed = search?.trim();

  const sorted = useMemo(() => {
    const opts = dataProps?.options || [];

    if (!dataProps.sort) return opts;

    return sortBy(opts, 'label', dataProps.sort);
  }, [dataProps.sort, dataProps.options]);

  const filtered = useMemo(() => {
    if (!trimmed) return sorted;

    return sorted.filter(
      (option) => {
        return option.label?.toLowerCase().includes(trimmed?.toLowerCase());
      },
      [trimmed]
    );
  }, [trimmed, sorted]);

  const data = useMemo(
    () => ({
      pageParams: [],
      pages: [
        {
          list: filtered,
          aggregate: filtered.length,
        },
      ],
    }),
    [filtered]
  );

  return (
    <SelectDropdown
      value={value}
      setValue={setValue} // overwrite setValue type
      error={dataProps.error || null}
      data={data}
      fetchNextPage={async () => {}}
      hasNextPage={false}
      // isFetching={dataProps.loading || false}
      isFetching={dataProps.loading ?? false}
      setSearch={dataProps.onSearchChange || setSearch}
      isFetchingNextPage={false}
      customBottomActions={customBottomActions}
      customActionTop={customActionTop}
      pinnedItems={pinnedItems}
      {...dropdownProps}
    />
  );
};
